import { X } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './Button';

/** A bottom sheet on phones and a centred dialog on desktop (GDD 08: small forms in bottom sheets). */
export function Sheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose(): void;
  children: ReactNode;
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => opener?.focus();
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center lg:items-center">
      <div
        data-testid="sheet-backdrop"
        aria-hidden
        className="absolute inset-0 bg-void/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        className="hud-cut-lg relative max-h-[85dvh] w-full overflow-y-auto border border-line-strong bg-panel p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] outline-none lg:max-w-md"
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 id={titleId} className="font-display text-lg font-bold uppercase tracking-[0.08em]">
            {title}
          </h2>
          <IconButton label="Close" onClick={onClose}>
            <X className="size-5" aria-hidden />
          </IconButton>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}
